import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import assert from '../../core/util/assert';
import notEmpty from '../../core/util/notEmpty';
import usePagination from './usePagination';

const calculateNextValue = (
    currentPrice: number,
    price: number,
    stepSize: number
) => {
    const isIncrease = price > currentPrice;

    return (
        (isIncrease
            ? Math.ceil(price / stepSize)
            : Math.floor(price / stepSize)) * stepSize
    );
};

const onUpdateMinPrice = ({
    stepSize,
    newMinPrice,
    maxPrice,
    shouldValidate,
}: {
    stepSize: number;
    newMinPrice?: number;
    maxPrice?: number;
    shouldValidate: boolean;
}) => {
    if (!newMinPrice || !shouldValidate) {
        return [newMinPrice, maxPrice];
    }

    if (notEmpty(maxPrice) && newMinPrice >= maxPrice) {
        return [newMinPrice, newMinPrice + stepSize];
    }

    return [newMinPrice, maxPrice];
};

const onUpdateMaxPrice = ({
    stepSize,
    newMaxPrice,
    minPrice,
    shouldValidate,
}: {
    stepSize: number;
    newMaxPrice?: number;
    minPrice?: number;
    shouldValidate: boolean;
}) => {
    if (!newMaxPrice || !shouldValidate) {
        return newMaxPrice;
    }

    if (notEmpty(minPrice) && newMaxPrice <= minPrice) {
        return minPrice + stepSize;
    }

    return newMaxPrice;
};

// eslint-disable-next-line max-statements, complexity
const computeUpdatedSearchParams = ({
    searchParams,
    minPrice,
    maxPrice,
    stepSize,
}: {
    searchParams: URLSearchParams;
    minPrice?: number;
    maxPrice?: number;
    stepSize: number;
}): [URLSearchParams | undefined, boolean] => {
    const newSearchParams = new URLSearchParams(searchParams);

    const currentMinPrice = newSearchParams.get('minPrice');
    const currentMaxPrice = newSearchParams.get('maxPrice');

    if (
        (currentMinPrice ? +currentMinPrice : undefined) === minPrice &&
        (currentMaxPrice ? +currentMaxPrice : undefined) === maxPrice
    ) {
        // Do not trigger updates when not needed.
        // This prevents scrolling to top when filters are applied
        return [undefined, false];
    }

    let shouldUpdateMaxPrice = false;

    if (minPrice) {
        newSearchParams.set('minPrice', minPrice.toString());

        if (notEmpty(maxPrice) && minPrice >= maxPrice) {
            const appliedMaxPrice = minPrice + stepSize;
            shouldUpdateMaxPrice = true;

            newSearchParams.set('maxPrice', appliedMaxPrice.toString());
        }
    } else {
        newSearchParams.delete('minPrice');
    }

    if (maxPrice) {
        if (notEmpty(minPrice) && minPrice >= maxPrice) {
            const appliedMaxPrice = minPrice + stepSize;
            shouldUpdateMaxPrice = true;

            newSearchParams.set('maxPrice', appliedMaxPrice.toString());
        } else {
            newSearchParams.set('maxPrice', maxPrice.toString());
        }
    } else {
        newSearchParams.delete('maxPrice');
    }

    return [newSearchParams, shouldUpdateMaxPrice];
};

const usePriceFilterState = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const initialMinPrice = searchParams.get('minPrice');
    const initialMaxPrice = searchParams.get('maxPrice');

    const appliedMinPrice = initialMinPrice ? +initialMinPrice : undefined;
    const appliedMaxPrice = initialMaxPrice ? +initialMaxPrice : undefined;

    const [minPrice, setMinPrice] = useState<number | undefined>(
        appliedMinPrice
    );
    const [maxPrice, setMaxPrice] = useState<number | undefined>(
        appliedMaxPrice
    );

    useEffect(() => {
        if (minPrice !== appliedMinPrice) {
            setMinPrice(appliedMinPrice);
        }

        if (maxPrice !== appliedMaxPrice) {
            setMaxPrice(appliedMaxPrice);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedMaxPrice, appliedMinPrice]);

    return {
        minPrice,
        setMinPrice,
        maxPrice,
        setMaxPrice,
        searchParams,
        setSearchParams,
    };
};

const usePriceFilter = (priceRange: { min: number; max: number }) => {
    const { minPrice, setMinPrice, maxPrice, setMaxPrice, searchParams } =
        usePriceFilterState();

    const stepSize = useMemo(
        () =>
            Math.max(
                Math.floor(((priceRange.max - priceRange.min) * 0.02) / 10) *
                    10,
                1
            ),
        [priceRange.max, priceRange.min]
    );

    const updateMinPrice = useCallback(
        (newMinPrice?: number, shouldValidate = false) => {
            const [updatedMinPrice, updatedMaxPrice] = onUpdateMinPrice({
                stepSize,
                newMinPrice,
                maxPrice,
                shouldValidate,
            });

            setMinPrice(updatedMinPrice);
            setMaxPrice(updatedMaxPrice);
        },
        [maxPrice, setMaxPrice, setMinPrice, stepSize]
    );

    const updateMaxPrice = useCallback(
        (newMaxPrice?: number, shouldValidate = false) => {
            setMaxPrice(
                onUpdateMaxPrice({
                    stepSize,
                    newMaxPrice,
                    minPrice,
                    shouldValidate,
                })
            );
        },
        [minPrice, setMaxPrice, stepSize]
    );

    const increaseMinPrice = useCallback(() => {
        const currentPrice = minPrice ?? 0;
        const finalPrice = calculateNextValue(
            currentPrice,
            currentPrice + 1,
            stepSize
        );

        updateMinPrice(finalPrice, true);
    }, [minPrice, stepSize, updateMinPrice]);

    const increaseMaxPrice = useCallback(() => {
        const currentPrice = maxPrice ?? 0;
        const finalPrice = calculateNextValue(
            currentPrice,
            currentPrice + 1,
            stepSize
        );

        updateMaxPrice(finalPrice, true);
    }, [maxPrice, stepSize, updateMaxPrice]);

    const decreaseMinPrice = useCallback(() => {
        if (!minPrice) {
            return;
        }

        const finalPrice = calculateNextValue(
            minPrice,
            Math.max(minPrice - 1, 0),
            stepSize
        );

        updateMinPrice(finalPrice, true);
    }, [minPrice, stepSize, updateMinPrice]);

    const decreaseMaxPrice = useCallback(() => {
        if (!maxPrice) {
            return;
        }

        const finalPrice = calculateNextValue(
            maxPrice,
            Math.max(maxPrice - 1, 0),
            stepSize
        );

        updateMaxPrice(finalPrice, true);
    }, [maxPrice, stepSize, updateMaxPrice]);
    const { navigateToPage } = usePagination();

    const applyFilter = useCallback(() => {
        const [updatedSearchParams, shouldUpdateMaxPrice] =
            computeUpdatedSearchParams({
                searchParams,
                minPrice,
                maxPrice,
                stepSize,
            });

        if (!updatedSearchParams) {
            return;
        }

        if (shouldUpdateMaxPrice) {
            const updatedMaxPrice = updatedSearchParams.get('maxPrice');
            assert(updatedMaxPrice, 'updatedMaxPrice is required.');
            setMaxPrice(+updatedMaxPrice);
        }

        navigateToPage(1, updatedSearchParams.toString());
    }, [
        navigateToPage,
        maxPrice,
        minPrice,
        searchParams,
        setMaxPrice,
        stepSize,
    ]);

    return {
        minPrice,
        maxPrice,
        stepSize,
        applyFilter,
        updateMinPrice,
        updateMaxPrice,
        increaseMinPrice,
        increaseMaxPrice,
        decreaseMinPrice,
        decreaseMaxPrice,
    };
};

export default usePriceFilter;
